import classNames from "classnames";
import { Button } from "components/ui/button";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Color } from "utils/theme";

interface SelectProps {
  options: { text: string; value: any; icon?: JSX.Element }[];
  selected: any;
  setSelected: (value: any) => void;
  className?: string;
  // Overrides the trigger button's default width classes
  triggerClassName?: string;
  variant?: "primary" | "secondary" | "outline" | "ghost" | "destructive";
  size?: "xs" | "sm" | "md" | "lg" | "icon";
}

export const Select = ({
  options,
  variant = "secondary",
  size = "md",
  selected,
  setSelected,
  className,
  triggerClassName,
}: SelectProps) => {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === selected);

  useEffect(() => {
    // add listener to body to close the dropdown when clicked outside
    const closeDropdown = () => {
      setOpen(false);
    };

    document.body.addEventListener("click", closeDropdown);

    return () => {
      document.body.removeEventListener("click", closeDropdown);
    };
  }, []);

  return (
    <div
      className={classNames("relative", className)}
      onClick={(e) => e.stopPropagation()}
    >
      <Button
        variant={variant}
        size={size}
        className={classNames(
          "flex items-center justify-between",
          triggerClassName ?? "min-w-[220px] w-full"
        )}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="flex items-center">
          {selectedOption?.icon && (
            <div className="mr-2">{selectedOption.icon}</div>
          )}
          <p>{selectedOption?.text || ""}</p>
        </span>
        <ChevronDown
          className={
            size === "xs" || size === "sm" ? "ml-2 size-4" : "ml-4 size-5"
          }
        />
      </Button>
      {open && (
        <div
          className="absolute shadow-lg top-full mt-1 right-0 p-1 z-50 border rounded-lg min-w-full w-max space-y-1"
          style={{
            background: Color.Primary[700],
            borderColor: Color.Primary[600],
          }}
        >
          {options.map((option) => (
            <Button
              key={option.value}
              size={size}
              variant={
                selectedOption?.value === option.value ? "primary" : "ghost"
              }
              onClick={() => {
                setSelected(option);
                setOpen(false);
              }}
              className="w-full border-0 rounded-md"
            >
              <div className="flex items-center justify-start w-full">
                {option.icon && <div className="mr-2">{option.icon}</div>}
                <p>{option.text}</p>
              </div>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
